import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import keyConstants from "../../constants/keyConstants";
import { TimeFormatIdentifiers } from "../../constants/timeFormats";
import dateWithComponents from "../../modules/definition/calendar/dateFunctions/dateWithComponents";
import { PropTypeShapes } from "../../types";
import { twoDigits } from "../../utils/numberToString";
import CalendarPickerRow from "./CalendarPickerRow";

const AM_LABEL = "am";
const PM_LABEL = "pm";

const getTimeInput = (value, onChange, onBlur, onKeyDown, onFocus) => (
  // When type="number", it will have a ridiculous width issue
  // using the type="text" size="1" now
  <input
    type="text"
    size="1"
    className="timeInput"
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    onFocus={onFocus}
  />
);

const getAmPmDropDown = (value, onChange, onBlur, onKeyDown, onFocus) => (
  <input
    type="text"
    size="1"
    className="amPmInput"
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    onFocus={onFocus}
  />
);

const getIs24 = calendar => {
  return calendar.timeFormat !== TimeFormatIdentifiers.Twelve;
};

const getHour = (date, calendar) => {
  const is24 = getIs24(calendar);
  if (isNil(date.hour)) {
    return is24 ? 0 : 12;
  } else if (is24) {
    return date.hour;
  } else {
    return date.hour === 0 ? 12 : date.hour > 12 ? date.hour - 12 : date.hour;
  }
};

// From display value to store value
const getAdjustedHour = (hour, calendar, isPM) => {
  const is24 = getIs24(calendar);
  if (is24) {
    return hour;
  } else {
    if (hour === 12) {
      return isPM ? 12 : 0;
    } else {
      return isPM ? hour + 12 : hour;
    }
  }
};

const getMinute = date => {
  return isNil(date.minute) ? 0 : date.minute;
};

const getSecond = date => {
  return isNil(date.second) ? 0 : date.second;
};

const getAmPm = (date, calendar) => {
  if (getIs24(calendar)) {
    return null;
  } else if (isNil(date.hour) || date.hour < 12) {
    return AM_LABEL;
  } else {
    return PM_LABEL;
  }
};

const validateHour = (str, date, calendar) => {
  const hour = parseInt(str);
  return !isNaN(hour) && hour >= 0 ? hour : getHour(date, calendar);
};

const validateMinute = (str, date) => {
  const minute = parseInt(str);
  return !isNaN(minute) && minute >= 0 && minute < 60 ? minute : getMinute(date);
};

const validateSecond = (str, date) => {
  const second = parseInt(str);
  return !isNaN(second) && second >= 0 && second < 60 ? second : getSecond(date);
};

class CalendarPickerSelectorTime extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      hour: getHour(props.date, props.calendar),
      minute: twoDigits(getMinute(props.date)),
      second: twoDigits(getSecond(props.date)),
      amPm: getAmPm(props.date, props.calendar)
    };

    this.onHourChange = this.onHourChange.bind(this);
    this.onMinuteChange = this.onMinuteChange.bind(this);
    this.onSecondChange = this.onSecondChange.bind(this);
    this.onAmPmChange = this.onAmPmChange.bind(this);
    this.onInputFinished = this.onInputFinished.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onAmPmKeyDown = this.onAmPmKeyDown.bind(this);
  }

  componentDidUpdate(prevProps) {
    this.onDateChange(prevProps.date, this.props.date, this.props.calendar);
  }

  onDateChange(prevDate, newDate, calendar) {
    if (
      isNil(newDate) ||
      isNil(prevDate) ||
      prevDate.timestamp !== newDate.timestamp ||
      prevDate.precision !== newDate.precision
    ) {
      this.setState({
        hour: getHour(newDate, calendar),
        minute: twoDigits(getMinute(newDate)),
        second: twoDigits(getSecond(newDate)),
        amPm: getAmPm(newDate, calendar)
      });
    }
  }

  onHourChange(event) {
    this.setState({ hour: event.target.value });
  }

  onMinuteChange(event) {
    this.setState({ minute: event.target.value });
  }

  onSecondChange(event) {
    this.setState({ second: event.target.value });
  }

  onAmPmChange(event) {
    event.target.select();
    const startChar = event.target.value.charAt(0).toLowerCase();
    const newValue = startChar === "a" ? AM_LABEL : startChar === "p" ? PM_LABEL : this.state.amPm;
    if (newValue !== this.state.amPm) {
      this.setState({ amPm: newValue });
      this.onInputFinished();
    }
  }

  onAmPmKeyDown(e) {
    switch (e.key) {
      case keyConstants.Tab:
      case keyConstants.Enter: {
        this.onInputFinished();
        break;
      }
      case "a":
      case "A": {
        this.setState({ amPm: AM_LABEL });
        break;
      }
      case "p":
      case "P": {
        this.setState({ amPm: PM_LABEL });
        break;
      }
      case keyConstants.Up:
      case keyConstants.Down:
      case keyConstants.Left:
      case keyConstants.Right:
        e.stopPropagation();
        break;
    }
  }

  onKeyDown(e) {
    switch (e.key) {
      case keyConstants.Tab:
      case keyConstants.Enter: {
        this.onInputFinished();
        break;
      }
      case keyConstants.Up:
      case keyConstants.Down:
      case keyConstants.Left:
      case keyConstants.Right:
        e.stopPropagation();
        break;
    }
  }

  onInputFinished() {
    const { date, calendar, onChange } = this.props;
    const { hour, minute, second, amPm } = this.state;
    const validHour = validateHour(hour, date, calendar);
    const validMinute = validateMinute(minute, date);
    const validSecond = validateSecond(second, date);
    const adjustedHour = getAdjustedHour(validHour, calendar, amPm === PM_LABEL);

    if (date.hour !== adjustedHour || date.minute !== validMinute || date.second !== validSecond) {
      // perform change
      const newDate = dateWithComponents(
        calendar,
        date.era,
        date.year,
        date.month,
        date.day,
        adjustedHour,
        validMinute,
        validSecond,
        date.precision
      );
      onChange(newDate);
    } else {
      // Fix the inputs
      this.setState({
        hour: validHour,
        minute: twoDigits(validMinute),
        second: twoDigits(validSecond)
      });
    }
  }

  onInputFocus(event) {
    event.target.select();
  }

  getTimeInput(value, onChange) {
    return getTimeInput(value, onChange, this.onInputFinished, this.onKeyDown, this.onInputFocus);
  }

  render() {
    const { calendar } = this.props;
    const is24 = calendar.timeFormat !== TimeFormatIdentifiers.Twelve;

    return (
      <CalendarPickerRow title={"Time"}>
        <div className={`calendarPickerSelectorTime`}>
          <div className="calendarPickerSelectorTimeContainer">
            {this.getTimeInput(this.state.hour, this.onHourChange)}
            <div className="separator">:</div>
            {this.getTimeInput(this.state.minute, this.onMinuteChange)}
            <div className="separator">:</div>
            {this.getTimeInput(this.state.second, this.onSecondChange)}
          </div>
          {is24
            ? null
            : getAmPmDropDown(
              this.state.amPm,
              this.onAmPmChange,
              this.onInputFinished,
              this.onAmPmKeyDown,
              this.onInputFocus
            )}
        </div>
      </CalendarPickerRow>
    );
  }
}

CalendarPickerSelectorTime.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  onChange: PropTypes.func.isRequired,
  date: PropTypeShapes.dateRelated
};

export default CalendarPickerSelectorTime;
